import React from 'react';
import styled from '@emotion/styled';

import Headings from '@components/Headings';
import Image, {ImagePlaceholder} from '@components/Image';

import mediaqueries from '@styles/media';
import {IArticle, IAuthor} from '@types';

import ArticleAuthors from './Article.Authors';
import VerificationBadge from "../../components/VerificationBadge";

interface ArticleHeroProps {
  article: IArticle;
  authors: IAuthor[];
}

const ArticleHero: React.FC<ArticleHeroProps> = ({ article, authors }) => {
  const hasCoAUthors = authors.length > 1;
  const hasHeroImage =
    article.hero &&
    Object.keys(article.hero.full).length !== 0 &&
    article.hero.full.constructor === Object;
  return (
    <Hero>
      <Header>
        <HeroHeading>{article.articleTitle || article.title}</HeroHeading>
        <HeroSubtitle hasCoAUthors={hasCoAUthors}>
          <ArticleAuthors authors={authors} />
          <ArticleMeta hasCoAUthors={hasCoAUthors}>
            {article.date} · {article.timeToRead} min read
          </ArticleMeta>
          <VerificationBadgeContainer>
            <VerificationBadge />
          </VerificationBadgeContainer>
        </HeroSubtitle>
      </Header>
      <HeroImage id="ArticleImage__Hero">
        {hasHeroImage ? (
          <Image src={article.hero.full} alt={article.title}/>
        ) : (
          <ImagePlaceholder />
        )}
      </HeroImage>
    </Hero>
  );
};

export default ArticleHero;

const Hero = styled.div`
  ${p => mediaqueries.phablet`
    &::after {
      content: "";
      width: 100%;
      height: 10px;
      background: ${p.theme.colors.background};
      position: absolute;
      left: 0;
      top: 10px;
      border-top-left-radius: 25px;
      border-top-right-radius: 25px;
      transition: ${p.theme.colorModeTransition};
    }
  `}
`;

const ArticleMeta = styled.div<{ hasCoAUthors: boolean }>`
  margin-left: ${p => (p.hasCoAUthors ? '10px' : '0')};

  ${mediaqueries.phablet`
    margin-left: 0;
  `}
`;

const VerificationBadgeContainer = styled.div`
  display: flex;
  flex: 1 0;
  justify-content: flex-end;
  
  ${mediaqueries.tablet`
    order: -1;
    justify-content: flex-start;
    margin-bottom: 10px;
  `}
`;

const Header = styled.header`
  position: relative;
  z-index: 10;
  margin: 0 auto 80px 64px;
  max-width: 738px;
  padding: 120px 0px 0 0px;

  ${mediaqueries.desktop_medium`
    margin: 0 24px 70px;
    max-width: 650px;
  `};
  
  ${mediaqueries.desktop`
    max-width: 100%;
    margin: 0 auto 35px 24px;
  `};
  
  ${mediaqueries.tablet`
    margin: 0 auto 70px;
    max-width: 100%;
  `}

  ${mediaqueries.phablet`
    margin: 0px auto 48px;
    padding: 104px 24px 0 24px;
  `}
`;

const HeroHeading = styled(Headings.h1)`
  font-size: 48px;
  font-family: ${p => p.theme.fonts.swis};
  margin-bottom: 25px;
  font-weight: 400;
  line-height: 1.28;
  letter-spacing: -0.02em;
  
  ${mediaqueries.tablet`
    margin-bottom: 0 24px 20px;
    font-size: 36px;
  `}

  ${mediaqueries.phablet`
    font-size: 32px;
  `}
`;

const HeroSubtitle = styled.div<{ hasCoAUthors: boolean }>`
  position: relative;
  display: flex;
  font-size: 18px;
  color: ${p => p.theme.colors.grey};

  ${p => mediaqueries.desktop`
      max-width: calc(100% - 24px);
      
    ${p.hasCoAUthors &&
  `
        &::before {
          left: 0 !important;
          right: 0 !important;
        }
    `}
  `}
  
  ${p => mediaqueries.desktop_medium`
    font-size: 14px;
    flex-direction: column;

    ${p.hasCoAUthors &&
  `
        &::before {
          content: '';
          position: absolute;
          left: -20px;
          right: -20px;
          top: -10px;
          bottom: -10px;
          opacity: 0.5;
          border-radius: 5px;
        }
    `}


    strong {
      display: block;
      font-weight: 500;
      margin-bottom: 5px;
    }
  `}
  
  ${p => mediaqueries.phablet`
    font-size: 14px;
    flex-direction: column;

    ${p.hasCoAUthors &&
      `
        &::before {
          content: '';
          position: absolute;
          left: -20px;
          right: -20px;
          top: -10px;
          bottom: -10px;
          border: 1px solid ${p.theme.colors.horizontalRule};
          opacity: 0.5;
          border-radius: 5px;
        }
    `}


    strong {
      display: block;
      font-weight: 500;
      margin-bottom: 5px;
    }
  `}
`;

const HeroImage = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 738px;
  overflow: hidden;
  margin-left: 64px;

  ${mediaqueries.desktop_medium`
    margin-left: 24px;
    max-width: 650px;
  `};

  ${mediaqueries.desktop`
    max-width: calc(100% - 48px);
    margin: 0 auto;
    padding-bottom: 40px;
  `};
  
  ${mediaqueries.tablet`
    max-width: 100%;
    margin: 0;
  `}

  ${mediaqueries.phablet`
    width: calc(100vw - 58px);
    margin: 0 auto;
    height: 220px;

    & > div {
      height: 220px;
    }
`}
`;
